import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { signOut } from '@/lib/auth-actions';

export type SignOutButtonProps = {
  /** Drives default label key + default className. */
  variant: 'public' | 'admin';
  /** Override the default class (defaults to btn btn--primary or admin sidebar style). */
  className?: string;
};

const VARIANT_LABEL_KEY: Record<SignOutButtonProps['variant'], string> = {
  public: 'nav.signOut',
  admin: 'admin.nav.signout',
};

const VARIANT_DEFAULT_CLASS: Record<SignOutButtonProps['variant'], string> = {
  public: 'btn btn--primary',
  admin: 'admin-sidebar__signout',
};

// Where to land after a successful sign-out. Public users go home; admin
// staff go back to the dedicated `/admin/login` page (Spec V1.1 §5.7).
const VARIANT_REDIRECT: Record<SignOutButtonProps['variant'], string> = {
  public: '/',
  admin: '/admin/login',
};

// Single Sign out surface mounted by PublicLayoutHeader and AdminLayout.
// Owns the ConfirmModal flow so the layouts stay declarative.
export function SignOutButton({ variant, className }: SignOutButtonProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleConfirm() {
    setSubmitting(true);
    try {
      await signOut();
      setConfirmOpen(false);
      navigate(VARIANT_REDIRECT[variant], { replace: true });
    } catch {
      toast.error(t('auth:errors.signOut', 'Could not sign out. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={className ?? VARIANT_DEFAULT_CLASS[variant]}
        onClick={() => setConfirmOpen(true)}
      >
        {t(VARIANT_LABEL_KEY[variant])}
      </button>
      {confirmOpen ? (
        <ConfirmModal
          title={t('auth:signOut.confirm.title')}
          body={t('auth:signOut.confirm.body')}
          confirmLabel={t('auth:signOut.confirm.confirmLabel')}
          onConfirm={() => {
            if (!submitting) void handleConfirm();
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      ) : null}
    </>
  );
}
